import React, { useState } from "react";
import {
  Box,
  Typography,
  useTheme as useMuiTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  getTableHeaderStyles,
  getTableContainerStyles,
  getTableRowStyles,
  getTableCellStyles,
} from "../styles/tableStyles";

/** Single agent/tool usage entry from usage_metrics.agent_usage (legacy flat list). */
export interface AgentUsageItem {
  tool_name?: string;
  agent_name?: string;
  invocation_count?: number;
  agent_timing?: number | string; // ms or formatted string from API
}

/** Tool usage item under each agent in usage_metrics.agents_usage */
export interface ToolUsageItem {
  tool_name?: string;
  invocation_count?: number;
}

/** Per-agent entry in usage_metrics.agents_usage (main_chat API). */
export interface AgentsUsageEntry {
  agent_name?: string;
  app?: boolean;
  timing_ms?: number;
  tool_usage?: ToolUsageItem[];
}

/** Shape of usage_metrics from /shay/email/main_chat draft response and messages API */
export interface DraftUsageMetricsData {
  confidence_score?: number;
  token_usage?: {
    input_tokens?: number;
    output_tokens?: number;
    total_tokens?: number;
    thought_tokens?: number;
  };
  /** Legacy: flat agent/tool list (messages + main_chat API) */
  agent_usage?: AgentUsageItem[];
  /** Per-agent usage with tool_usage array (main_chat API); used for tools-used table */
  agents_usage?: AgentsUsageEntry[];
}

interface DraftUsageMetricsProps {
  usageMetrics: DraftUsageMetricsData | null | undefined;
  /** Border position: "top" for messages page, "bottom" for compose. Default "bottom". */
  borderPosition?: "top" | "bottom";
}

/**
 * Formats a token count for display (e.g. 12965 -> "13K", 1_500_000 -> "1.5M", 500 -> "500").
 */
const formatTokenCount = (value: number | undefined): string => {
  if (value === undefined || value === null) return "—";
  if (value >= 1_000_000) {
    const m = value / 1_000_000;
    return m % 1 === 0 ? `${m}M` : `${m.toFixed(1)}M`;
  }
  if (value >= 1000) {
    const k = value / 1000;
    return k % 1 === 0 ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return String(value);
};

/**
 * Formats agent timing from ms to seconds: 1 digit after decimal; whole numbers without decimal (e.g. 8, 0, 11.9).
 */
const formatAgentTimingSec = (ms: number): string => {
  const sec = ms / 1000;
  const rounded = Math.round(sec * 10) / 10;
  return rounded % 1 === 0 ? String(Math.round(rounded)) : rounded.toFixed(1);
};

/**
 * Returns confidence dot color by percentage: 0-50 red, 50-70 orange, 70-100 green.
 */
const getConfidenceDotColor = (percentage: number): string => {
  if (percentage < 50) return "#d32f2f"; // red
  if (percentage < 70) return "#ffa500"; // orange
  return "#2e7d32"; // green
};

/** Dot size used for both confidence indicator and separators (consistent for all). */
const DOT_SIZE = 7;

/** Dot separator between metrics (same size as confidence dot). */
const DotSeparator = () => (
  <Box
    sx={{
      width: DOT_SIZE,
      height: DOT_SIZE,
      borderRadius: "50%",
      backgroundColor: "grey.500",
      flexShrink: 0,
    }}
  />
);

/**
 * Displays confidence score and token usage in a row below the draft editor.
 * Format: label: value. No dark chip; dot separators between items; input/output tokens only.
 */
const DraftUsageMetrics: React.FC<DraftUsageMetricsProps> = ({ usageMetrics, borderPosition = "bottom" }) => {
  const muiTheme = useMuiTheme();
  const useTopBorder = borderPosition === "top";
  const [toolsModalOpen, setToolsModalOpen] = useState(false);

  if (!usageMetrics) {
    return null;
  }

  const confidenceScore = usageMetrics.confidence_score;
  const tokenUsage = usageMetrics.token_usage;
  const hasConfidence =
    typeof confidenceScore === "number" && !Number.isNaN(confidenceScore);
  const inputStr =
    tokenUsage && typeof tokenUsage.input_tokens === "number"
      ? formatTokenCount(tokenUsage.input_tokens)
      : null;
  const outputStr =
    tokenUsage && typeof tokenUsage.output_tokens === "number"
      ? formatTokenCount(tokenUsage.output_tokens)
      : null;
  const hasTokenParts = inputStr !== null || outputStr !== null;
  // agents_usage: one row per agent; each agent has tool_usage[] (multiple tools)
  const agentsUsage = Array.isArray(usageMetrics.agents_usage) ? usageMetrics.agents_usage : [];
  // Flattened list of all tools (for summary "Tools used: first, +N more")
  const flattenedToolNames: string[] = [];
  agentsUsage.forEach((agent) => {
    const tools = Array.isArray(agent.tool_usage) ? agent.tool_usage : [];
    tools.forEach((t) => {
      if (t.tool_name) flattenedToolNames.push(t.tool_name);
    });
  });
  // Legacy: flat agent_usage (messages + main_chat API)
  const agentUsage = Array.isArray(usageMetrics.agent_usage) ? usageMetrics.agent_usage : [];
  const hasAgentUsage = agentsUsage.length > 0 || agentUsage.length > 0;

  if (!hasConfidence && !hasTokenParts && !hasAgentUsage) {
    return null;
  }

  // Match Sources section: same font size, bold labels, text.secondary
  const labelSx = {
    color: "text.secondary",
    fontSize: "0.75rem",
    fontWeight: 600,
  };
  const valueSx = {
    color: "text.secondary",
    fontSize: "0.75rem",
    fontWeight: 400,
  };

  // Tight gap between dot and its label; larger gap between the three metric groups
  const groupGap = 0.5;
  const betweenGroupsGap = 2.5;
  // Messages page (top): smaller mt so gap from Sources above isn't excessive when both show
  const spacing = useTopBorder
    ? { mt: 0.5, pt: 1.5, pb: 1.5, borderTop: `1px solid ${muiTheme.palette.divider}` }
    : { mt: 0.75, pt: 0.75, pb: 1.5, borderBottom: `1px solid ${muiTheme.palette.divider}` };

  return (
    <Box
      sx={{
        ...spacing,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: betweenGroupsGap,
      }}
    >
      {hasConfidence && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: groupGap }}>
          {/* Confidence indicator dot: red (0-50%), orange (50-70%), green (70-100%) */}
          <Box
            sx={{
              width: DOT_SIZE,
              height: DOT_SIZE,
              borderRadius: "50%",
              backgroundColor: getConfidenceDotColor(Math.round(Number(confidenceScore) * 100)),
              flexShrink: 0,
            }}
          />
          <Typography component="span" variant="caption" sx={labelSx}>
            Confidence score:{" "}
          </Typography>
          <Typography component="span" variant="caption" sx={valueSx}>
            {Math.round(Number(confidenceScore) * 100)}%
          </Typography>
        </Box>
      )}
      {inputStr !== null && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: groupGap }}>
          <DotSeparator />
          <Typography component="span" variant="caption" sx={labelSx}>
            Input tokens:{" "}
          </Typography>
          <Typography component="span" variant="caption" sx={valueSx}>
            {inputStr}
          </Typography>
        </Box>
      )}
      {outputStr !== null && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: groupGap }}>
          <DotSeparator />
          <Typography component="span" variant="caption" sx={labelSx}>
            Output tokens:{" "}
          </Typography>
          <Typography component="span" variant="caption" sx={valueSx}>
            {outputStr}
          </Typography>
        </Box>
      )}
      {hasAgentUsage && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: groupGap }}>
          <DotSeparator />
          <Typography component="span" variant="caption" sx={labelSx}>
            Tools used:{" "}
          </Typography>
          <Typography component="span" variant="caption" sx={valueSx}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                ...valueSx,
                ...(flattenedToolNames.length > 0 || agentUsage.length > 0
                  ? { color: "primary.main", cursor: "pointer", "&:hover": { opacity: 0.9 } }
                  : {}),
              }}
              onClick={() => (flattenedToolNames.length > 0 || agentUsage.length > 0) && setToolsModalOpen(true)}
              role={flattenedToolNames.length > 0 || agentUsage.length > 0 ? "button" : undefined}
              aria-label={flattenedToolNames.length > 0 || agentUsage.length > 0 ? "View tools used details" : undefined}
            >
              {flattenedToolNames.length > 0
                ? (flattenedToolNames[0] ?? "—")
                : (agentUsage[0]?.tool_name ?? "—")}
            </Typography>
            {(flattenedToolNames.length > 1 || (flattenedToolNames.length === 0 && agentUsage.length > 1)) && (
              <>
                ,{" "}
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ...valueSx,
                    color: "primary.main",
                    cursor: "pointer",
                    "&:hover": { opacity: 0.9 },
                  }}
                  onClick={() => setToolsModalOpen(true)}
                  role="button"
                  aria-label={`Show ${flattenedToolNames.length > 0 ? flattenedToolNames.length : agentUsage.length} tools used`}
                >
                  +{(flattenedToolNames.length > 0 ? flattenedToolNames.length : agentUsage.length) - 1} more
                </Typography>
              </>
            )}
          </Typography>
        </Box>
      )}
      {/* Modal: table restructured by agents_usage — one row per agent, with Tools used (multiple tool_name + invocation_count) */}
      <Dialog
        open={toolsModalOpen}
        onClose={() => setToolsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
        aria-labelledby="tools-used-dialog-title"
      >
        <DialogTitle id="tools-used-dialog-title" sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 2 }}>
          Tools used
          <IconButton aria-label="Close" onClick={() => setToolsModalOpen(false)} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          <TableContainer component={Paper} sx={{ ...getTableContainerStyles(), backgroundColor: "background.paper", boxShadow: 2 }}>
            <Table stickyHeader aria-label="Tools used table">
              <TableHead>
                <TableRow>
                  {agentsUsage.length > 0 ? (
                    <>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Agent Name</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Agent Timing (Sec)</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Tools used & Invocation count</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Tool Name</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Agent Name</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Invocation count</TableCell>
                      <TableCell sx={{ ...getTableHeaderStyles(muiTheme.palette.mode === "dark"), ...getTableCellStyles() }}>Agent Timing (Sec)</TableCell>
                    </>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {agentsUsage.length > 0
                  ? agentsUsage.map((agent, idx) => {
                      const tools = Array.isArray(agent.tool_usage) ? agent.tool_usage : [];
                      return (
                        <TableRow key={idx} hover sx={getTableRowStyles()}>
                          <TableCell sx={getTableCellStyles()}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                              <Typography component="span" variant="body2">
                                {agent.agent_name ?? "—"}
                              </Typography>
                              {agent.app === true && (
                                <Chip
                                  label="App"
                                  size="small"
                                  sx={{
                                    height: 20,
                                    fontSize: "0.7rem",
                                    backgroundColor: muiTheme.palette.primary.main,
                                    color: "#fff",
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            {typeof agent.timing_ms === "number"
                              ? formatAgentTimingSec(agent.timing_ms)
                              : (agent.timing_ms != null ? String(agent.timing_ms) : "—")}
                          </TableCell>
                          <TableCell sx={getTableCellStyles()}>
                            {tools.length > 0 ? (
                              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                {tools.map((t, toolIdx) => (
                                  <Chip
                                    key={toolIdx}
                                    size="small"
                                    label={
                                      <>
                                        <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                          {t.tool_name ?? "—"} (
                                        </Typography>
                                        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                                          {t.invocation_count ?? "—"}
                                        </Typography>
                                        <Typography component="span" variant="body2" sx={{ fontWeight: 400 }}>
                                          )
                                        </Typography>
                                      </>
                                    }
                                    sx={{ fontSize: "0.75rem" }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  : agentUsage.map((row, idx) => (
                      <TableRow key={idx} hover sx={getTableRowStyles()}>
                        <TableCell sx={getTableCellStyles()}>{row.tool_name ?? "—"}</TableCell>
                        <TableCell sx={getTableCellStyles()}>{row.agent_name ?? "—"}</TableCell>
                        <TableCell sx={getTableCellStyles()}>{row.invocation_count ?? "—"}</TableCell>
                        <TableCell sx={getTableCellStyles()}>
                          {typeof row.agent_timing === "number"
                            ? formatAgentTimingSec(row.agent_timing)
                            : (row.agent_timing != null ? String(row.agent_timing) : "—")}
                        </TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default DraftUsageMetrics;
